import type { DaySummaryDto } from '@fos/shared/contracts/copilot';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useScope } from '../../../lib/scope';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Skeleton } from '../../../ui/States';
import { Tooltip } from '../../../ui/Tooltip';
import { copilotErrorInfo } from '../copilot-ui';
import { useCopilotInvalidate, useCopilotStatus } from '../hooks';
import { CopilotFootnote } from './CopilotFootnote';

/**
 * „Podsumuj dzień” w nagłówku Dziś (Figma 01: przycisk Secondary z ikoną sparkles). Okno z 2–3 zdaniami i najważniejszymi
 * punktami z danych Dziś (akceptacje, zadania, alarmy, „Wymaga Ciebie”, plan, mój czas) w zakresie osoby — bez kwot.
 * Wynik tylko do odczytu (nic nie jest zapisywane poza historią AI). Zakres „Moje / Zespół” z przełącznika w menu.
 * AI wyłączone = przycisk nieaktywny z podpowiedzią; reszta Dziś działa.
 */
export function DaySummaryButton({ scope: scopeProp }: { scope?: 'mine' | 'team' } = {}) {
  const { scope: shellScope } = useScope();
  const scope = scopeProp ?? shellScope;
  const status = useCopilotStatus();
  const run = trpc.copilot.summarizeDay.useMutation();
  const invalidate = useCopilotInvalidate();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<DaySummaryDto | null>(null);

  const disabled = status.data ? !status.data.available : false;

  const start = async () => {
    setOpen(true);
    setResult(null);
    try {
      setResult(await run.mutateAsync({ scope }));
      void invalidate.jobs();
    } catch {
      // błąd w oknie
    }
  };

  const button = (
    <Button
      variant="secondary"
      icon={Sparkles}
      onClick={() => void start()}
      disabled={disabled}
      className="max-md:h-11"
    >
      Podsumuj dzień
    </Button>
  );
  const error = run.isError ? copilotErrorInfo(run.error) : null;

  return (
    <>
      {disabled ? (
        <Tooltip content={status.data?.message ?? 'AI jest niedostępne'}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Podsumowanie dnia"
        description={scope === 'team' ? 'Zespół, w Twoim zakresie danych.' : undefined}
        footer={
          <>
            {error && !run.isPending && (
              <Button variant="ghost" onClick={() => void start()}>
                Spróbuj ponownie
              </Button>
            )}
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Zamknij
            </Button>
          </>
        }
      >
        {run.isPending && (
          <div role="status" aria-label="AI pracuje" className="flex flex-col gap-2">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-11/12" />
            <Skeleton className="h-3.5 w-3/5" />
          </div>
        )}
        {error && !run.isPending && <FormMessage>{error.message}</FormMessage>}
        {result && !run.isPending && (
          <div className="flex flex-col gap-3">
            <p className="text-body-m text-fg">{result.summary}</p>
            {result.highlights.length > 0 && (
              <ul className="list-disc pl-5 text-body-s text-fg-secondary">
                {result.highlights.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            )}
            <CopilotFootnote kind="Podsumowanie AI" meta={result.meta} sources={result.sources} />
          </div>
        )}
      </Dialog>
    </>
  );
}
